import type { LocationQuery } from '#vue-router'
import type { ExperimentNameUnion, ExperimentValue, ExperimentVersions } from '../types'

function isVersionKey(key: string | undefined, versions: ExperimentVersions): key is ExperimentValue {
    return !!key && Object.keys(versions).includes(key)
}

// Any configured version: a concluded test keeps only its winner, which can be B.
export function experimentRun(versions: ExperimentVersions): string {
    return Object.values(versions).find(Boolean)?.split('_').pop() ?? ''
}

/**
 * Reads an experiment cookie against the current config. `b.31` is valid for run 31 only, so
 * bumping the test number re-draws every visitor. A bare `b` (set by hand, or written before
 * cookies carried the run) counts for the current run. Anything else is null.
 */
export function parseExperimentCookie(value: unknown, versions: ExperimentVersions): ExperimentValue | null {
    if (typeof value !== 'string') return null

    const [variant, run] = value.split('.')

    if (!isVersionKey(variant, versions)) return null
    if (run !== undefined && run !== experimentRun(versions)) return null

    return variant
}

export function formatExperimentCookie(variant: ExperimentValue, versions: ExperimentVersions): string {
    return `${variant}.${experimentRun(versions)}`
}

/**
 * The arm `?experiment=signup:b,landingPage:c` asks for one experiment, so the team can check each
 * arm without editing cookies and marketing can send visitors into one. `useExperiment` ignores
 * unconfigured arms.
 */
export function experimentOverride(query: LocationQuery, experimentName: ExperimentNameUnion): string | undefined {
    const param = Array.isArray(query.experiment) ? query.experiment[0] : query.experiment

    if (!param) return undefined

    return param.split(',').map(pair => pair.split(':')).find(([name]) => name === experimentName)?.[1]
}
